import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  TextField, IconButton, CircularProgress, Card, CardContent,
  Grid, Stack, MenuItem, Divider
} from "@mui/material";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  HistoryEdu as ExamIcon
} from "@mui/icons-material";

import Swal from "sweetalert2";
import { db } from "../../../firebase";
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy
} from "firebase/firestore";

const PRIMARY_INDIGO = "#6366F1";

function Examtypes() {
  const [examTypes, setExamTypes] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    title: "",
    academicYear: "", // Fetched from 'academicyears' collection
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Academic Years for the dropdown
      const yearSnap = await getDocs(query(collection(db, "academicyears"), orderBy("year", "desc")));
      const years = yearSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAcademicYears(years);

      // Set default year if available
      if (years.length > 0) {
        setForm(prev => ({ ...prev, academicYear: years[0].year }));
      }

      // 2. Fetch Exam Types
      await fetchExamTypes();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchExamTypes = async () => {
    const q = query(collection(db, "exam_types"), orderBy("title", "asc"));
    const snap = await getDocs(q);
    setExamTypes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const handleSubmit = async () => {
    if (!form.title ) {
      Swal.fire("Error", "Please fill all fields", "error");
      return;
    }

    setLoading(true);
    try {
      if (editId) {
        await updateDoc(doc(db, "exam_types", editId), form);
        Swal.fire("Updated", "Exam type updated", "success");
      } else {
        await addDoc(collection(db, "exam_types"), form);
        Swal.fire("Saved", "New exam type added", "success");
      }
      resetForm();
      fetchExamTypes();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    const res = await Swal.fire({
      title: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
    });

    if (res.isConfirmed) {
      await deleteDoc(doc(db, "exam_types", id));
      fetchExamTypes();
    }
  };

  const resetForm = () => {
    setForm({ 
      title: "", 
      academicYear: academicYears.length > 0 ? academicYears[0].year : "" 
    });
    setEditId(null);
    setOpen(false);
  };

  return (
    <Box p={1} sx={{ backgroundColor: "#F8FAFC", minHeight: "100vh" }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight="900" color="#1E293B">Exam Types</Typography>
          <Typography variant="caption" color="text.secondary">Manage academic session exam categories</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={fetchExamTypes}><RefreshIcon fontSize="small" /></IconButton>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            sx={{ bgcolor: PRIMARY_INDIGO, textTransform: 'none', borderRadius: 2 }}
          >
            Add Type
          </Button>
        </Stack>
      </Stack>

      {/* Small Cards */}
      {loading && examTypes.length === 0 ? (
        <Box textAlign="center" py={5}><CircularProgress size={30} /></Box>
      ) : (
        <Grid className="row" spacing={1.5}>
          {examTypes.map((exam) => (
            <Grid className="col-sm-4 mb-3" item xs={12} sm={6} md={4} lg={3} key={exam.id}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" fontWeight="800" color="#1E293B">{exam.title}</Typography>
                      <Typography variant="caption" color={PRIMARY_INDIGO} fontWeight="700">
                        Session: {exam.academicYear} 2026-27
                      </Typography>
                    </Box>
                    <Stack direction="row">
                      <IconButton size="small" onClick={() => { setForm(exam); setEditId(exam.id); setOpen(true); }}>
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(exam.id)}>
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Simplified Modal */}
      <Dialog open={open} onClose={resetForm} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: "900", fontSize: '1rem', display: 'flex', justifyContent: 'space-between' }}>
          {editId ? "Edit Exam Type" : "New Exam Type"}
          <IconButton onClick={resetForm} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} mt={1}>
            

            <TextField
              label="Exam Title"
              placeholder="e.g. Unit Test - I"
              size="small"
              fullWidth
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />

            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              sx={{ bgcolor: PRIMARY_INDIGO, py: 1, fontWeight: 'bold' }}
              disabled={loading}
            >
              {editId ? "Update" : "Save Exam Type"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Examtypes;